import { isPresent } from "@apl-digital/utils";
import * as Sentry from "@sentry/react";
import i18n from "i18next";

import { isLoginEnabled } from "@/api/authSession";
import { ERROR_STATUSES, getGenericAPIError, getResponse } from "@/api/helpers";
import type {
  ApiClientDiagnostics,
  ApiResourceTiming,
  FetchApiOptions,
  FetchRequestOptions,
  FetchWithErrorsOptions,
  RequestResponse,
  ResponseFailed,
} from "@/api/types";
import { ContentType, RequestHeader, RequestMethod } from "@/api/types";
import { getUserManager } from "@/api/userManager";
import { getTenantKey } from "@/base/utils/tenantKey";
import config from "@/constants/config";

type NetworkInformation = {
  effectiveType?: string;
  type?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
};

type NavigatorWithConnection = Navigator & {
  connection?: NetworkInformation;
  mozConnection?: NetworkInformation;
  webkitConnection?: NetworkInformation;
};

type RequestDiagnostics = {
  client: ApiClientDiagnostics;
  resourceTiming?: ApiResourceTiming;
};

type ResponseRequestIdDiagnostics = {
  responseRequestId?: string;
  responseRequestIdEchoed: boolean;
};

type RequestSnapshotOptions = {
  options: Omit<FetchApiOptions, "signal" | "language">;
  requestHeaders: Headers;
  language: string | null | undefined;
  requestId: string;
  responseRequestIdDiagnostics: ResponseRequestIdDiagnostics;
  diagnostics: RequestDiagnostics;
  hasBody: boolean;
};

const getErrorTextProperty = (
  error: unknown,
  property: "message" | "name",
): string | undefined => {
  if (error instanceof Error) {
    return error[property];
  }

  if (typeof error !== "object" || error === null || !(property in error)) {
    return undefined;
  }

  const value = (error as Record<typeof property, unknown>)[property];
  return typeof value === "string" ? value : undefined;
};

const getSignalAbortReason = (signal: AbortSignal | undefined) => {
  if (!signal?.aborted) {
    return;
  }

  const signalWithReason = signal as { readonly reason?: unknown };
  const reason = signalWithReason.reason;

  return (
    getErrorTextProperty(reason, "name") ??
    getErrorTextProperty(reason, "message") ??
    (typeof reason === "string" ? reason : "AbortError")
  );
};

const getHeaderValue = (
  headers: Record<string, string> | undefined,
  headerName: string,
) => {
  const normalizedHeaderName = headerName.toLowerCase();

  return Object.entries(headers ?? {}).find(
    ([key]) => key.toLowerCase() === normalizedHeaderName,
  )?.[1];
};

const createRequestId = () =>
  globalThis.crypto?.randomUUID?.() ??
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

const getClientDiagnostics = (): ApiClientDiagnostics => {
  const navigatorWithConnection =
    typeof navigator === "undefined"
      ? undefined
      : (navigator as NavigatorWithConnection);
  const connection =
    navigatorWithConnection?.connection ??
    navigatorWithConnection?.mozConnection ??
    navigatorWithConnection?.webkitConnection;

  return {
    online: navigatorWithConnection?.onLine,
    visibilityState:
      typeof document === "undefined" ? undefined : document.visibilityState,
    connectionEffectiveType: connection?.effectiveType,
    connectionType: connection?.type,
    downlink: connection?.downlink,
    rtt: connection?.rtt,
    saveData: connection?.saveData,
  };
};

const getCurrentPerformanceTime = () =>
  typeof performance === "undefined" ? undefined : performance.now();

const getResourceTiming = (
  apiPath: string,
  requestStartTime: number | undefined,
): ApiResourceTiming | undefined => {
  if (typeof performance === "undefined" || requestStartTime === undefined) {
    return undefined;
  }

  const resourceTiming = performance
    .getEntriesByName(apiPath, "resource")
    .find((entry) => entry.startTime >= requestStartTime) as
    | PerformanceResourceTiming
    | undefined;

  if (!resourceTiming) {
    return undefined;
  }

  const bodySizeAvailable =
    resourceTiming.transferSize > 0 ||
    resourceTiming.encodedBodySize > 0 ||
    resourceTiming.decodedBodySize > 0;

  return {
    bodySizeAvailable,
    durationMs: Math.round(resourceTiming.duration),
    ...(bodySizeAvailable
      ? {
          transferSize: resourceTiming.transferSize,
          encodedBodySize: resourceTiming.encodedBodySize,
          decodedBodySize: resourceTiming.decodedBodySize,
        }
      : {}),
    nextHopProtocol: resourceTiming.nextHopProtocol,
  };
};

const getRequestDiagnostics = (
  apiPath: string,
  requestStartTime: number | undefined,
): RequestDiagnostics => ({
  client: getClientDiagnostics(),
  resourceTiming: getResourceTiming(apiPath, requestStartTime),
});

const getResponseRequestIdDiagnostics = (
  response: Response | undefined,
  requestId: string,
): ResponseRequestIdDiagnostics => {
  const responseRequestId =
    response?.headers.get(RequestHeader.REQUEST_ID) ?? undefined;

  return {
    responseRequestId,
    responseRequestIdEchoed: responseRequestId === requestId,
  };
};

const sensitiveSnapshotHeaders = new Set([
  RequestHeader.AUTHORIZATION.toLowerCase(),
  "cookie",
  "set-cookie",
  "x-api-key",
]);

const getSnapshotHeaders = (headers: Headers): Record<string, string> => {
  const snapshotHeaders: Record<string, string> = {};
  const headerEntries = [...headers.entries()] as [
    key: string,
    value: string,
  ][];

  for (const [key, value] of headerEntries) {
    if (!sensitiveSnapshotHeaders.has(key.toLowerCase())) {
      snapshotHeaders[key] = value;
    }
  }

  return snapshotHeaders;
};

const getBodySnapshotMetadata = (
  body: FetchApiOptions["body"],
  hasBody: boolean,
): Pick<ResponseFailed["request"], "bodyType" | "hasBody"> =>
  hasBody
    ? {
        bodyType: body instanceof FormData ? "form_data" : "json",
        hasBody: true,
      }
    : {};

const getRequestSnapshot = ({
  options: { body, ...options },
  requestHeaders,
  language,
  requestId,
  responseRequestIdDiagnostics,
  diagnostics,
  hasBody,
}: RequestSnapshotOptions): ResponseFailed["request"] => ({
  ...options,
  ...getBodySnapshotMetadata(body, hasBody),
  headers: getSnapshotHeaders(requestHeaders),
  language,
  requestId,
  responseRequestId: responseRequestIdDiagnostics.responseRequestId,
  responseRequestIdEchoed: responseRequestIdDiagnostics.responseRequestIdEchoed,
  client: diagnostics.client,
  resourceTiming: diagnostics.resourceTiming,
});

const getFetchFailureReason = (
  error: unknown,
  signal: AbortSignal | undefined,
) => {
  if (!signal?.aborted) {
    return "Network error";
  }

  const abortReason = getSignalAbortReason(signal);
  const errorName = getErrorTextProperty(error, "name");

  if (abortReason === "TimeoutError" || errorName === "TimeoutError") {
    return "Request timed out";
  }

  return "Request aborted";
};

const fetchApi = async <TSuccess, TError>({
  signal,
  // web standards use - instead of underscore
  // https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/lang
  language = i18n.language.replace("_", "-"),
  ...options
}: FetchApiOptions): RequestResponse<TSuccess, TError> => {
  const { method, api, shouldParseResponse, body, headers } = options;

  const isFormData = body instanceof FormData;
  const hasBody =
    [RequestMethod.POST, RequestMethod.PUT].includes(method) &&
    body !== undefined;
  const requestBody = hasBody
    ? isFormData
      ? body
      : JSON.stringify(body)
    : undefined;

  const apiPath = `${config.apiConfig.orderAppProxy?.url}${api}`;
  const requestId =
    getHeaderValue(headers, RequestHeader.REQUEST_ID) ?? createRequestId();

  const requestHeaders = new Headers({
    ...headers,
    [RequestHeader.TENANT_KEY]: getTenantKey(),
  });
  requestHeaders.set(RequestHeader.REQUEST_ID, requestId);

  if (isLoginEnabled()) {
    const user = await getUserManager(i18n.language).getUser();

    if (user?.access_token) {
      requestHeaders.append(
        RequestHeader.AUTHORIZATION,
        `Bearer ${user.access_token}`,
      );
    }
  }

  if (isPresent(language)) {
    requestHeaders.append(RequestHeader.ACCEPTED_LANGUAGE, language);
  }

  if (!isFormData) {
    requestHeaders.append(
      RequestHeader.CONTENT_TYPE,
      ContentType.APPLICATION_JSON,
    );
  }

  const requestStartTime = getCurrentPerformanceTime();

  try {
    const response = await fetch(apiPath, {
      signal,
      method,
      headers: requestHeaders,
      ...(hasBody && { body: requestBody }),
    });

    if (ERROR_STATUSES.includes(response.status)) {
      const responseRequestIdDiagnostics = getResponseRequestIdDiagnostics(
        response,
        requestId,
      );
      const diagnostics = getRequestDiagnostics(apiPath, requestStartTime);

      Sentry.addBreadcrumb({
        category: "api",
        message: `${method} ${api} — ${response.status}`,
        level: response.status >= 500 ? "error" : "warning",
        data: {
          method,
          url: api,
          status_code: response.status,
          request_id: requestId,
          response_request_id: responseRequestIdDiagnostics.responseRequestId,
          response_request_id_echoed:
            responseRequestIdDiagnostics.responseRequestIdEchoed,
        },
      });

      return getGenericAPIError(
        "Error response status",
        getRequestSnapshot({
          options,
          requestHeaders,
          language,
          requestId,
          responseRequestIdDiagnostics,
          diagnostics,
          hasBody,
        }),
        response,
      );
    }

    if (shouldParseResponse) {
      try {
        const responseData = (await response.json()) as TSuccess;

        return getResponse<TSuccess>(responseData, response);
      } catch {
        const responseRequestIdDiagnostics = getResponseRequestIdDiagnostics(
          response,
          requestId,
        );
        const diagnostics = getRequestDiagnostics(apiPath, requestStartTime);

        Sentry.addBreadcrumb({
          category: "api",
          message: `${method} ${api} — response parse error`,
          level: "error",
          data: {
            method,
            url: api,
            status_code: response.status,
            request_id: requestId,
            response_request_id: responseRequestIdDiagnostics.responseRequestId,
            response_request_id_echoed:
              responseRequestIdDiagnostics.responseRequestIdEchoed,
          },
        });

        return getGenericAPIError(
          "Error parsing response",
          getRequestSnapshot({
            options,
            requestHeaders,
            language,
            requestId,
            responseRequestIdDiagnostics,
            diagnostics,
            hasBody,
          }),
          response,
        );
      }
    }

    return getResponse<TSuccess>(null as TSuccess, response);
  } catch (error) {
    const failureReason = getFetchFailureReason(error, signal);
    const diagnostics = getRequestDiagnostics(apiPath, requestStartTime);
    const responseRequestIdDiagnostics = getResponseRequestIdDiagnostics(
      undefined,
      requestId,
    );

    Sentry.addBreadcrumb({
      category: "api",
      message: `${method} ${api} — ${failureReason}`,
      level: "error",
      data: {
        aborted: signal?.aborted ?? false,
        abort_reason: getSignalAbortReason(signal),
        error_message: getErrorTextProperty(error, "message"),
        error_name: getErrorTextProperty(error, "name"),
        method,
        request_id: requestId,
        client: diagnostics.client,
        resource_timing: diagnostics.resourceTiming,
        url: api,
      },
    });

    return getGenericAPIError(
      failureReason,
      getRequestSnapshot({
        options,
        requestHeaders,
        language,
        requestId,
        responseRequestIdDiagnostics,
        diagnostics,
        hasBody,
      }),
    );
  }
};

const fetchWithErrors = async <TSuccess, TError>({
  shouldParseResponse = true,
  canFail = false,
  ...options
}: FetchWithErrorsOptions): RequestResponse<TSuccess, TError> => {
  let signal = options.signal;
  let timeoutId: NodeJS.Timeout | undefined;

  if (!isPresent(options.signal)) {
    const abortController = new AbortController();

    timeoutId = setTimeout(() => {
      Sentry.captureMessage(
        `Request timeout: ${options.method ?? "GET"} ${options.api} exceeded ${config.apiConfig.orderAppProxy.timeout}ms`,
        {
          level: "warning",
          fingerprint: ["request-timeout", options.api],
          tags: {
            api_endpoint: options.api,
            http_method: options.method ?? "GET",
          },
          extra: {
            api: options.api,
            method: options.method,
            timeout: config.apiConfig.orderAppProxy.timeout,
          },
        },
      );
      abortController.abort(
        new DOMException("Request timed out", "TimeoutError"),
      );
    }, config.apiConfig.orderAppProxy.timeout);

    signal = abortController.signal;
  }

  const response = await fetchApi<TSuccess, TError>({
    ...options,
    signal,
    shouldParseResponse,
  });

  clearTimeout(timeoutId);

  if (canFail || response.isResponseOk) {
    return response;
  }

  return response;
};

export const request = async <TSuccess, TError = void>(
  options: FetchRequestOptions,
): RequestResponse<TSuccess, TError> =>
  fetchWithErrors({ ...options, method: RequestMethod.GET });

export const post = async <TSuccess, TError = void>(
  options: FetchRequestOptions,
): RequestResponse<TSuccess, TError> =>
  fetchWithErrors({
    ...options,
    method: RequestMethod.POST,
  });

export const put = async <TSuccess, TError = void>(
  options: FetchRequestOptions,
): RequestResponse<TSuccess, TError> =>
  fetchWithErrors({
    ...options,
    method: RequestMethod.PUT,
  });

export const deleteRequest = async <TSuccess, TError = void>(
  options: FetchRequestOptions,
): RequestResponse<TSuccess, TError> =>
  fetchWithErrors({
    ...options,
    method: RequestMethod.DELETE,
  });
